import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ReactComponent as EditIcons } from "../../../assets/icons/edit.svg";
import { ReactComponent as LeftArrowIcons } from "../../../assets/icons/left-arrow.svg";
import { ReactComponent as TrashCanIcons } from "../../../assets/icons/trash-can.svg";
import { AuthContext } from "../../../contexts/AuthContext";
import { categoryService } from "../../../services/api/categoryService";
import { firebaseService } from "../../../services/api/firebaseService";
import { formatDate } from "../../../utils/DateFormat";
import Loading from "../components/Loading";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

const formatData = (item) => ({
  createDate: formatDate(item.createdAt || new Date().toISOString()),
  name: item.name || "Chưa có tên",
  image: item.image || "/images/logo.png",
  detail: item.description || "Không có mô tả",
  modifiedDate: formatDate(item.updatedAt || new Date().toISOString()),
  nav_id: item.id || null,
  originalImage: item.image || "", // Keep for cleanup
  originalDescription: item.description || "", // Keep for cleanup
});

const DetailCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, user } = useContext(AuthContext);

  const [canDelete, setCanDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /** Extract Firebase image URLs from HTML content */
  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && (src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/"))) {
        firebaseUrls.push(src);
      }
    });
    return firebaseUrls;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await categoryService.get_by_id(id, token);
      setData(formatData(response));
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response?.data?.message ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);

      // 1. Get all associated images
      const logoUrl = data?.originalImage;
      const contentImages = extractFirebaseImageUrls(data?.originalDescription);
      const allImagesToDelete = [...contentImages];
      if (logoUrl) allImagesToDelete.push(logoUrl);

      // 2. Delete from database
      await categoryService.delete_category(id, token);

      // 3. Cleanup all images from Firebase
      for (const url of allImagesToDelete) {
        if (url.startsWith("https://storage.googleapis.com/") || url.startsWith("https://firebasestorage.googleapis.com/")) {
          try {
            await firebaseService.delete_file(token, url);
          } catch (err) {
            console.warn("Lỗi xóa file Firebase:", url, err);
          }
        }
      }

      toast.success(`Xóa loại dịch vụ ${data?.name || ""} thành công!`);
      navigate("/loai-dich-vu");
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response?.data?.message ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Error:", error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [fetchData, user?.role]);

  if (loading)
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );

  if (!data) return <div>Không có dữ liệu để hiển thị.</div>;

  return (
    <div className="h-full overflow-auto">
      <div className="bg-white rounded-lg p-6 border-b h-full">
        <h1 className="text-center font-sf-semibold text-[#0457C2] text-2xl uppercase border-b p-4">
          Chi tiết {data.name}
        </h1>

        <div className="flex items-center px-[5vw] py-6">
          <div>
            <div className="flex items-center">
              <strong className="w-40">Hình ảnh loại dịch vụ :</strong>
              <img
                src={data.image}
                alt="logo"
                className="size-24 rounded-xl object-contain"
              />
            </div>
            <div className="flex items-start">
              <strong className="w-40">Tên loại dịch vụ :</strong>
              <span> {data.name}</span>
            </div>
            <div className="flex items-start">
              <strong className="w-40">Ngày tạo :</strong>
              <span>{data.createDate}</span>
            </div>
            <div className="flex items-start">
              <strong className="w-40">Ngày chỉnh sửa :</strong>
              <span>{data.modifiedDate}</span>
            </div>
          </div>
        </div>

        <div className="px-[5vw] py-6">
          <strong className="text-gray-700">Nội dung mô tả:</strong>
          <div
            className="p-6 border rounded-lg mt-3 min-h-[300px] prose max-w-none bg-gray-50"
            dangerouslySetInnerHTML={{ __html: data.detail }}
          />
        </div>

        {/* Button Action */}
        <div className="mt-6 flex justify-end space-x-4 px-[5vw]">
          <button
            onClick={() => navigate("/loai-dich-vu")}
            className="bg-blue-500 text-white p-2 pr-4 rounded-lg flex items-center hover:bg-blue-600"
          >
            <LeftArrowIcons />
            Quay lại
          </button>

          <button
            type="button"
            onClick={() => navigate("/loai-dich-vu/" + id + "/chinh-sua")}
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-300 hover:text-white flex gap-2"
          >
            <EditIcons />
            Chỉnh sửa
          </button>

          {canDelete ? (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="border-red-500 text-red-500 border rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-red-500 hover:text-white"
            >
              <TrashCanIcons />
              Xoá loại dịch vụ
            </button>
          ) : null}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => (loading ? null : setShowDeleteModal(false))}
        onConfirm={handleConfirmDelete}
        loading={loading}
        title="Xác nhận xóa"
        message={`Bạn có chắc chắn muốn xóa "${data.name}" không?`}
      />
    </div>
  );
};

export default DetailCategory;
